"use client"

import { useState } from "react"
import Link from "next/link"
import { Minus, Plus } from "lucide-react"
import { rutaDeLaCesta } from "@/lib/mercado/rutas"
import { euros } from "@/lib/mercado/texto"
import type { Idioma } from "@/lib/landing/idioma"
import { anadirALaCesta, leerCesta } from "./cesta-local"
import { textosMercado } from "./textos"

/**
 * Elegir formato y cantidad, y a la cesta.
 *
 * El disponible que se ve aqui puede tener unos minutos. No pasa nada: el que
 * manda se comprueba en servidor al abrir el pago, y si mientras tanto se ha
 * vendido, se dice ahi.
 */

export interface FormatoComprable {
  id: string
  titulo: string | null
  precio: number
  disponible: number
}

export function Comprar({
  vendedor,
  formatos,
  sePuedeComprar,
  idioma = "es",
}: {
  vendedor: { id: string; nombre: string }
  formatos: FormatoComprable[]
  /** El vendedor tiene el pago con tarjeta listo. */
  sePuedeComprar: boolean
  idioma?: Idioma
}) {
  const t = textosMercado(idioma)
  const RUTA_CESTA = rutaDeLaCesta(idioma)
  const primero = formatos.find((f) => f.disponible > 0) ?? formatos[0]
  const [elegido, setElegido] = useState<string>(primero?.id ?? "")
  const [cantidad, setCantidad] = useState(1)
  const [estado, setEstado] = useState<"nada" | "anadido" | "otro_vendedor">("nada")

  const formato = formatos.find((f) => f.id === elegido) ?? primero
  if (!formato) return <p className="text-sm text-zinc-500">{t.sinFormatos}</p>

  const tope = Math.min(50, formato.disponible)
  const agotado = tope <= 0

  function anadir(vaciarAntes = false) {
    if (!formato) return
    const resultado = anadirALaCesta(vendedor, formato.id, Math.min(cantidad, tope), vaciarAntes)
    setEstado(resultado)
  }

  return (
    <div>
      <p className="text-3xl tabular-nums text-zinc-950">{euros(formato.precio, idioma)}</p>

      {formatos.length > 1 && (
        <fieldset className="mt-6">
          <legend className="text-sm font-medium text-zinc-950">{t.formato}</legend>
          <div className="mt-3 flex flex-wrap gap-2">
            {formatos.map((f) => {
              const activo = f.id === formato.id
              return (
                <button
                  key={f.id}
                  type="button"
                  aria-pressed={activo}
                  onClick={() => {
                    setElegido(f.id)
                    setCantidad(1)
                    setEstado("nada")
                  }}
                  className={`rounded-full border px-4 py-2 text-sm transition-colors ${
                    activo ? "border-zinc-950 bg-zinc-950 text-white" : "border-zinc-300 text-zinc-800 hover:border-zinc-500"
                  } ${f.disponible <= 0 ? "line-through opacity-60" : ""}`}
                >
                  {f.titulo ?? t.unico}
                </button>
              )
            })}
          </div>
        </fieldset>
      )}

      {!sePuedeComprar ? (
        <p className="mt-6 rounded-2xl border border-zinc-200 p-4 text-sm text-zinc-600">
          {t.terminandoDeActivar(vendedor.nombre)}
        </p>
      ) : agotado ? (
        <p className="mt-6 text-sm font-medium text-zinc-700">{t.agotadoPorAhora}</p>
      ) : (
        <>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <div className="inline-flex items-center rounded-full border border-zinc-300">
              <button
                type="button"
                aria-label={t.quitarUno}
                className="p-3 text-zinc-700 disabled:opacity-30"
                disabled={cantidad <= 1}
                onClick={() => setCantidad((c) => Math.max(1, c - 1))}
              >
                <Minus className="h-4 w-4" aria-hidden />
              </button>
              <span className="w-8 text-center text-sm tabular-nums" aria-live="polite">
                {Math.min(cantidad, tope)}
              </span>
              <button
                type="button"
                aria-label={t.anadirUno}
                className="p-3 text-zinc-700 disabled:opacity-30"
                disabled={cantidad >= tope}
                onClick={() => setCantidad((c) => Math.min(tope, c + 1))}
              >
                <Plus className="h-4 w-4" aria-hidden />
              </button>
            </div>
            <button
              type="button"
              onClick={() => anadir()}
              className="rounded-full bg-zinc-950 px-7 py-3 text-sm font-medium text-white transition-colors hover:bg-zinc-800"
            >
              {t.anadirALaCesta}
            </button>
          </div>
          {tope <= 5 && <p className="mt-3 text-sm text-zinc-500">{t.quedan(tope)}</p>}
        </>
      )}

      {estado === "anadido" && (
        <p className="mt-4 text-sm text-zinc-700" role="status">
          {t.anadido}{" "}
          <Link href={RUTA_CESTA} className="font-medium text-zinc-950 underline underline-offset-4">
            {t.verCestaYPagar}
          </Link>
        </p>
      )}

      {estado === "otro_vendedor" && (
        <div className="mt-4 rounded-2xl border border-zinc-200 p-4 text-sm text-zinc-700" role="alert">
          <p>{t.otroVendedorEnCesta(leerCesta().vendedorNombre ?? t.otroVendedor)}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => anadir(true)}
              className="rounded-full bg-zinc-950 px-4 py-2 text-sm font-medium text-white hover:bg-zinc-800"
            >
              {t.vaciarYAnadir}
            </button>
            <Link href={RUTA_CESTA} className="rounded-full border border-zinc-300 px-4 py-2 text-sm font-medium text-zinc-900 hover:border-zinc-500">
              {t.terminarAntes}
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}
